// frontend/src/context/AuthContext.jsx
import { createContext, useState, useEffect, useRef } from 'react';
import logger from '../utils/logger';

// Create context outside component to avoid Fast Refresh issues
const AuthContext = createContext();

export { AuthContext };

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // Start with null
  const [authLoading, setAuthLoading] = useState(true); // Add loading state
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null); // New state for user data
  const inactivityTimer = useRef(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      fetchUserProfile(storedToken).finally(() => setAuthLoading(false));
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only set up inactivity logout if user is authenticated
    if (!token) return;

    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      // 30 minutes of inactivity before logout (1800000ms)
      inactivityTimer.current = setTimeout(() => {
        alert('Logged out due to inactivity.');
        logout();
        window.location.reload();
      }, 1800000); // 30 minutes = 1800000ms
    };

    // List of events to listen for to consider as activity
    const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keydown'];

    // Reset timer on any event
    events.forEach((event) => window.addEventListener(event, resetTimer));

    // Initialize timer
    resetTimer();

    // Clean up
    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [token]);

  const login = async (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setIsAuthenticated(true);
    setAuthLoading(true); // Set loading while fetching profile
    try {
      await fetchUserProfile(newToken);
    } finally {
      setAuthLoading(false); // Always set loading to false after profile is fetched
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
  };

  const fetchUserProfile = async (authToken) => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        logger.error('Failed to fetch user profile:', res.statusText);
        setUser(null);
        setIsAuthenticated(false);
        logout();
      }
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      setUser(null);
      setIsAuthenticated(false);
      logout();
    }
  };

  const refreshUser = () => {
    if (token) return fetchUserProfile(token);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, authLoading, token, user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}